/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Column } from '../../layout/flexbox';
import { ResortToolsLogo } from '../../branding/ResortToolsLogo';
import { Loader } from '../../forms/elements/loader/Loader';
import * as Buttons from '../../forms/elements/buttons';
import { FADE_IN_ANIMATION } from '../../styles/animations';
import { COOL_GREY, RED_CREEK, WHITE } from '../../styles/colors';
import { MediumText } from '../../styles/styles';
import { getResource } from '../../language/resources/getResource';

type Props = {
  isLoading: boolean;
  authError?: Error;
  resources: Record<string, any>;
  fromPath?: string;
  onLogin: (fromPath?: string) => void;
};

const LoginButton = styled(Buttons.BigNeutral)`
  margin-left: auto;
` as any;

const Root = styled('div')`
  ${FADE_IN_ANIMATION};
  border: solid ${COOL_GREY} 1px;
  border-radius: 10px;
  padding: 50px;
  background-color: ${WHITE};
`;

const widthStyle = css`
  width: 300px;
  @media (max-width: 420px) {
    width: 200px;
  }
`;

export const ErrorPlaceholder = styled('div')`
  margin-bottom: 0px;
  min-height: 0px;
  ${widthStyle};
`;

const ErrorText = styled('span')`
  ${FADE_IN_ANIMATION};
  color: ${RED_CREEK};
  ${MediumText};
  margin-bottom: 20px;
  min-height: 20px;
  ${widthStyle};
`;

const auth0Loader = css`
  width: 100px;
  height: 100px;
  margin-top: 82px;
  margin-bottom: 82px;
`;

const loginButton = (isLoading: boolean) => css`
  pointer-events: ${isLoading ? 'none' : 'auto'};
  transition: 0.25s ease-in-out;
`;

export const Auth0Login: React.FC<Props> = ({
  isLoading,
  authError,
  resources,
  fromPath,
  onLogin,
}) => {
  const isBlocked = !!authError && !!authError.message?.match(/blocked.*/);

  return (
    <Root>
      <Column alignItems="center" rootCss={css`position: relative;`}>
        <ResortToolsLogo resources={resources} />
        {isBlocked ? (
          <ErrorText id="login-error-text">
            {getResource(resources, 'AUTH0_LOGIN_ERROR')}
          </ErrorText>
        ) : (
          <ErrorPlaceholder />
        )}

        <Column rootCss={loginButton(isLoading)}>
          {!isLoading ? (
            <LoginButton
              type="submit"
              id="login-button"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                onLogin(fromPath);
              }}
              disabled={isLoading}
              title={getResource(resources, 'AUTH0_LOGIN_TITLE')}
            >
              {getResource(resources, 'AUTH0_LOGIN')}
            </LoginButton>
          ) : (
            <Loader rootCss={auth0Loader} />
          )}
        </Column>
      </Column>
    </Root>
  );
};
